import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StreakService } from '../../services/StreakService';
import { XpService } from '../../services/XpService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { ChunkyButton, GameProgressBar, Confetti, ComboPill, ShakeWidget, FloatingXpBadge } from '../../components/GameUI';

// Drag-and-drop vocabulary matching mini-game — port of the mobile app's
// WordMatchScreen. Pointer-events based (not native HTML5 drag-and-drop) so
// the same code works with mouse and touch.

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function WordCard({ text, dir, matched, hovering, wrong, dragging, style, innerRef, ...rest }) {
  let bg = '#FFFFFF', border = '#E2E8F0', fg = '#0F172A';
  if (matched) { bg = '#D7FFB8'; border = '#58CC02'; fg = '#2B6B00'; }
  else if (wrong) { bg = '#FFDFE0'; border = '#FF4B4B'; fg = '#8B0000'; }
  else if (hovering) { bg = '#EEF2FF'; border = '#4F46E5'; fg = '#0F172A'; }
  else if (dragging) { bg = '#FFFFFF'; border = '#CE82FF'; fg = '#0F172A'; }

  return (
    <div
      ref={innerRef}
      dir={dir}
      style={{
        background: bg,
        borderColor: border,
        color: fg,
        boxShadow: dragging ? '0 6px 14px rgba(0,0,0,0.18)' : undefined,
        touchAction: 'none',
        ...style,
      }}
      className="flex h-14 items-center justify-center rounded-2xl border-[2.5px] px-3.5 text-center text-sm font-black leading-tight transition-colors"
      {...rest}
    >
      {matched && <span className="mr-1.5 text-[#58CC02]">✓</span>}
      <span className="truncate">{text}</span>
    </div>
  );
}

export default function WordMatchGame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { pairs = [], nativeLang = { dir: 'ltr' }, targetLang = { dir: 'ltr' }, langPair = 'guest' } = state || {};

  const [leftOrder, setLeftOrder] = useState(() => shuffle(pairs));
  const [rightOrder, setRightOrder] = useState(() => shuffle(pairs));
  const [matchedIds, setMatchedIds] = useState(new Set());
  const [wrongTargetId, setWrongTargetId] = useState(null);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [done, setDone] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [comboPopKey, setComboPopKey] = useState(0);
  const [dragId, setDragId] = useState(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const [hoverId, setHoverId] = useState(null);

  const rewardedRef = useRef(false);
  const targetRefs = useRef({});

  useEffect(() => {
    if (!pairs.length) navigate('/games', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const xpEarned = matchedIds.size * 4 + (mistakes === 0 ? 10 : 0);

  function awardRewards() {
    if (rewardedRef.current) return;
    rewardedRef.current = true;
    StreakService.recordActivity();
    XpService.addXp(xpEarned);
    if (mistakes === 0) SoundEffectService.playCelebration();
  }

  function restart() {
    setLeftOrder(shuffle(pairs));
    setRightOrder(shuffle(pairs));
    setMatchedIds(new Set());
    setWrongTargetId(null);
    setCombo(0);
    setMaxCombo(0);
    setMistakes(0);
    setDone(false);
    setHasInteracted(false);
    rewardedRef.current = false;
  }

  function handleDrop(draggedId, targetPair) {
    if (matchedIds.has(targetPair.id)) return;
    if (draggedId === targetPair.id) {
      const next = new Set(matchedIds);
      next.add(targetPair.id);
      setMatchedIds(next);
      setCombo((c) => {
        const nc = c + 1;
        setMaxCombo((mc) => Math.max(mc, nc));
        if (nc >= 2) setComboPopKey((k) => k + 1);
        return nc;
      });
      SoundEffectService.playCorrect();
      if (navigator.vibrate) navigator.vibrate(15);
      LearningProgressService.record(langPair, 'vocabulary', 1, { activityId: `wordmatch:${targetPair.id}` });
      if (next.size === pairs.length) {
        setDone(true);
      }
    } else {
      setWrongTargetId(targetPair.id);
      setCombo(0);
      setMistakes((m) => m + 1);
      SoundEffectService.playWrong();
      if (navigator.vibrate) navigator.vibrate(35);
      setTimeout(() => setWrongTargetId(null), 450);
    }
  }

  useEffect(() => {
    if (done) awardRewards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  function findTargetIdAt(x, y) {
    for (const [id, el] of Object.entries(targetRefs.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return id;
    }
    return null;
  }

  function startDrag(pair, e) {
    if (matchedIds.has(pair.id)) return;
    if (!hasInteracted) setHasInteracted(true);
    const point = e.touches ? e.touches[0] : e;
    setDragId(pair.id);
    setDragPos({ x: point.clientX, y: point.clientY });

    function onMove(ev) {
      const p = ev.touches ? ev.touches[0] : ev;
      setDragPos({ x: p.clientX, y: p.clientY });
      setHoverId(findTargetIdAt(p.clientX, p.clientY));
    }
    function onUp(ev) {
      const p = ev.changedTouches ? ev.changedTouches[0] : ev;
      const targetId = findTargetIdAt(p.clientX, p.clientY);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      setDragId(null);
      setHoverId(null);
      if (targetId) {
        const targetPair = pairs.find((pr) => pr.id === targetId);
        if (targetPair) handleDrop(pair.id, targetPair);
      }
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }

  const draggingPair = useMemo(() => pairs.find((p) => p.id === dragId), [pairs, dragId]);

  if (!pairs.length) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-lg px-4 py-4 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50">
            ← {t('games.title')}
          </button>
          <h1 className="text-sm font-black text-slate-900">{t('games.wordMatch')}</h1>
          <span className="w-16" />
        </div>

        {done ? (
          <div className="relative flex flex-col items-center py-10 text-center">
            {mistakes === 0 && <Confetti />}
            <span className="text-6xl">{mistakes === 0 ? '🏆' : '🎉'}</span>
            <h2 className="mt-3 text-2xl font-black text-slate-900">{t('games.wordMatchComplete')}</h2>
            <p className="mt-1 text-sm text-slate-500">{t('games.wordMatchBestCombo', { combo: maxCombo })}</p>
            <div className="mt-3"><FloatingXpBadge xp={xpEarned} /></div>
            <div className="mt-8 w-full max-w-xs">
              <ChunkyButton color="#4F46E5" onClick={restart} className="w-full py-4 text-center text-white">
                {t('games.tryAgain')}
              </ChunkyButton>
              <button onClick={() => navigate('/games')} className="mt-3 w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">
                {t('games.done')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {!hasInteracted && (
              <div className="mb-3 flex items-center gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-3.5 py-2.5">
                <span className="text-lg">👆</span>
                <p className="text-xs font-bold text-indigo-700">{t('games.wordMatchDragHint')}</p>
              </div>
            )}

            <div className="mb-2 flex items-center justify-between">
              <span className="text-base font-black text-slate-900">{matchedIds.size}/{pairs.length}</span>
              {combo >= 2 && <ComboPill key={comboPopKey} combo={combo} />}
            </div>
            <GameProgressBar value={pairs.length ? matchedIds.size / pairs.length : 0} color="#4F46E5" />

            <div className="mt-5 grid grid-cols-2 gap-3.5">
              <div className="flex flex-col gap-3">
                {leftOrder.map((pair) => matchedIds.has(pair.id) ? (
                  <div key={pair.id} className="h-14" />
                ) : (
                  <div
                    key={pair.id}
                    onPointerDown={(e) => startDrag(pair, e)}
                    onTouchStart={(e) => startDrag(pair, e)}
                    style={{ opacity: dragId === pair.id ? 0.3 : 1, cursor: 'grab' }}
                  >
                    <WordCard text={pair.target} dir={targetLang.dir} />
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-3">
                {rightOrder.map((pair) => (
                  <ShakeWidget key={pair.id} trigger={wrongTargetId === pair.id}>
                    <WordCard
                      innerRef={(el) => { targetRefs.current[pair.id] = el; }}
                      text={pair.native}
                      dir={nativeLang.dir}
                      matched={matchedIds.has(pair.id)}
                      hovering={hoverId === pair.id}
                      wrong={wrongTargetId === pair.id}
                    />
                  </ShakeWidget>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {draggingPair && (
        <div
          className="pointer-events-none fixed z-50"
          style={{ left: dragPos.x - 80, top: dragPos.y - 28, width: 160 }}
        >
          <WordCard text={draggingPair.target} dir={targetLang.dir} dragging />
        </div>
      )}
    </div>
  );
}
